// janus-engine/resources/resource-adjust.ts — Lane L3 (2026-09-26): the pure resolvers for the GM / sheet resource tools.
// Browser decisions are saved as typed raw columns; optimistic, receipt and observer
// installs patch the same held vitality cluster through its native formula.
import { patchVitality } from '../compile/vitality-build.js';
import { createVitalityReader } from '../compile/vitality-reader.js';
import { readItemField } from '../compile/item-field-reader.js';
import { storedStatusRows } from '../compile/creature-status-reader.js';
import { createStatusStackReader } from '../compile/status-stack-reader.js';
import contract from '../compile/c1-contract.json';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import type { BaseEnvelope, BasePatchOp } from '../compile/base-patch';
import type { AppliedFrame, AppliedTouchedRow } from '../transport/applied-frame';
import { encounterCreatureField } from '../../shared/array-types';
import { normalizePhpListish } from '../../shared/php-listish';
import { compileCreatureApsTotal } from '../compile/creature-meta-defaults';

export const RESOURCE_SCHEMA = 'janus.resource.truth.v1';
export type ResourceKind = 'add_ap' | 'add_reaction' | 'weapon_attack' | 'head_attack' | 'luck' | 'mana' | 'hunger' | 'thirst'
  | 'temp_v' | 'max_temp_v' | 'max_v' | 'v' | 'heal_to_max'
  /** R-124 (2026-10-01): the out-of-combat rest decided by janus-engine/economy/out-of-combat.ts — silent, no log row; never
   *  resolved here (resolveResourceAdjust answers unknown-kind), posted through tryCommitResolvedResource. */
  | 'ooc_rest';
export type ResourceColumn = 'aps' | 'reaction' | 'weapon_attack' | 'head_attack' | 'luck' | 'mana' | 'hunger_meter_current'
  | 'thirst_meter_current' | 'v_temp' | 'v_max_temp' | 'v_max' | 'v_current' | 'encounter_creature_dead';
export type ResourceValues = Partial<Record<ResourceColumn, number>>;
export const RESOURCE_KIND_COLUMNS: Readonly<Record<ResourceKind, readonly ResourceColumn[]>> = {
  add_ap: ['aps'], add_reaction: ['reaction'], weapon_attack: ['weapon_attack'], head_attack: ['head_attack'], luck: ['luck'],
  mana: ['mana'], hunger: ['hunger_meter_current'], thirst: ['thirst_meter_current'], temp_v: ['v_temp'],
  max_temp_v: ['v_max_temp', 'v_current'], max_v: ['v_max', 'v_current'], v: ['v_current'], heal_to_max: ['v_current'],
  ooc_rest: ['aps', 'reaction'],
};
/** The kinds whose legacy endpoint ran remove_recovery_and_dying_states / resurrect_creature: `before` also pins the dead flag. */
export const RESOURCE_TAIL_KINDS: ReadonlySet<ResourceKind> = new Set(['v', 'heal_to_max']);

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
/** PHP `(int)` of a stored scalar: null / '' / absent → 0. */
const int = (value: unknown): number => { const n = Number(scalar(value) ?? 0); return Number.isFinite(n) ? Math.trunc(n) : 0; };

export interface ResourceReaders {
  /** layer_value($row, key): null when the layer is absent (the PHP comparisons against null differ from 0). */
  layer: (row: Row, key: string) => unknown;
  /** layer_has($row, key). */
  hasLayer: (row: Row, key: string) => boolean;
}
export interface ResourceInput { kind: ResourceKind; delta?: number }
export type ResourceResolution =
  | { supported: true; before: ResourceValues; after: ResourceValues; delta: number; logType: string }
  | { supported: false; refused: true; message: string }
  | { supported: false; refused: false; reason: string };

/** PHP `$x > $cap` where $cap came from layer_value (null when absent). A null cap compares as bool: any truthy $x is greater. */
export function aboveCap(value: number, cap: unknown): boolean {
  const c = scalar(cap);
  if (c === null || c === undefined || c === '') return value !== 0;
  return value > Number(c);
}
export const capValue = (cap: unknown): number => { const c = scalar(cap); return c === null || c === undefined || c === '' ? 0 : Math.trunc(Number(c)); };

/** Persisted resource columns and reader-merged vitality (including lean definitions). */
function readState(row: Row) {
  const ec = asRow(row.encounter_creatures) ?? asRow(row.inventory) ?? {};
  const v = (readVitality(row) as Row | null) ?? {};
  const vit = (key: string, fallbackBase?: string): number => int(v[key] ?? (fallbackBase ? v[fallbackBase] : undefined));
  return { ec, vit };
}

export function resolveResourceAdjust(row: Row, input: ResourceInput, readers: ResourceReaders): ResourceResolution {
  const { ec, vit } = readState(row);
  const delta = Math.trunc(Number(input.delta ?? 0));
  if (!Number.isFinite(delta)) return { supported: false, refused: false, reason: 'delta-not-numeric' };
  const dead = int(ec.encounter_creature_dead) ? 1 : 0;
  const ok = (before: ResourceValues, after: ResourceValues, logType: string, d = delta): ResourceResolution => ({ supported: true, before, after, delta: d, logType });
  switch (input.kind) {
    case 'add_ap': {
      // add_ap_inc.php: +1, no cap (current AP may exceed aps_total); the row builder stores at most 127.
      const aps = int(ec.aps);
      if (aps >= 127) return { supported: false, refused: false, reason: 'aps-ceiling' };
      return ok({ aps }, { aps: aps + 1 }, 'add_ap', 1);
    }
    case 'add_reaction': {
      const reaction = int(ec.reaction);
      if (reaction === 1) return { supported: false, refused: true, message: 'You are already have your reaction!' };
      return ok({ reaction }, { reaction: 1 }, 'add_reaction', 1);
    }
    case 'weapon_attack':
    case 'head_attack': {
      const col = input.kind;
      if (col === 'head_attack' && int(readers.layer(row, 'count_head_weapon_equipped')) < 1) return { supported: false, refused: true, message: 'No head weapon equipped.' };
      const current = int(ec[col]);
      const next = Math.min(10, Math.max(0, current + delta));
      const noun = col === 'weapon_attack' ? 'weapon attacks' : 'head attacks';
      if (next === current) return { supported: false, refused: true, message: delta > 0 ? `You are already at max ${noun} (${current}).` : `You have no ${noun} to reduce.` };
      return ok({ [col]: current }, { [col]: next }, (delta > 0 ? 'increased_' : 'decreased_') + col);
    }
    case 'luck': {
      // adjust_luck_inc.php: +delta, capped at the `fortune` layer, floored at −1.
      const current = int(ec.luck);
      const fortune = readers.layer(row, 'fortune');
      let next = current + delta;
      if (aboveCap(next, fortune)) next = capValue(fortune);
      if (next < -1) next = -1;
      return ok({ luck: current }, { luck: next }, 'self_adjust_luck');
    }
    case 'mana': {
      // adjust_mana_inc.php: +delta, floored at 0, capped at the `spirit` LAYER (mana_percent divides by the COLUMN).
      const current = int(ec.mana);
      let next = current + delta;
      if (next < 0) next = 0;
      const spirit = readers.layer(row, 'spirit');
      if (aboveCap(next, spirit)) next = capValue(spirit);
      return ok({ mana: current }, { mana: next }, 'self_adjust_mana');
    }
    case 'hunger':
    case 'thirst': {
      const col = input.kind === 'hunger' ? 'hunger_meter_current' : 'thirst_meter_current';
      const current = int(ec[col]);
      const cap = readers.layer(row, input.kind === 'hunger' ? 'hunger_meter_max' : 'thirst_meter_max');
      let next = current + delta;
      if (aboveCap(next, cap)) next = capValue(cap);
      if (next < 0) next = 0;
      return ok({ [col]: current }, { [col]: next }, input.kind === 'hunger' ? 'self_adjust_hunger' : 'self_adjust_thirst');
    }
    case 'temp_v': {
      // adjust_temp_v_inc.php: +delta, floored at 0 (owner 2026-09-27: temp V cannot be negative — the engine's set_v_temp floor).
      const current = int(ec.v_temp);
      return ok({ v_temp: current }, { v_temp: Math.max(0, current + delta) }, 'self_adjust_temp_v');
    }
    case 'max_temp_v':
    case 'max_v': {
      // N6 (2026-10-01): no hand-back for a dead row or a change that takes V to 0 or below — the legacy endpoints
      // (adjust_max_v_inc.php / adjust_v_max_temp_inc.php) wrote exactly these two columns in those cases too, with no tail
      // (no death, no dying status, no revive); the L3 "Tier-1 boundary" hand-back was caution, not a rule they ran.
      const current = vit('v_current');
      let vMax = vit('v_max', 'base_v_max'), vMaxTemp = vit('v_max_temp');
      if (input.kind === 'max_temp_v') vMaxTemp += delta; else vMax += delta;
      const total = vMax + vMaxTemp;
      const nextCurrent = total < current ? total : current;
      if (input.kind === 'max_v' && vMax < 0) vMax = 0; // adjust_max_v_inc.php floors v_max AFTER the clamp
      return input.kind === 'max_temp_v'
        ? ok({ v_max_temp: int(ec.v_max_temp), v_current: int(ec.v_current) }, { v_max_temp: vMaxTemp, v_current: nextCurrent }, 'self_adjust_temp_max_v')
        : ok({ v_max: int(ec.v_max), v_current: int(ec.v_current) }, { v_max: vMax, v_current: nextCurrent }, 'self_adjust_max_v');
    }
    case 'v':
    case 'heal_to_max': {
      const current = vit('v_current');
      const effectiveMax = vit('effective_v_max');
      let next: number, d: number;
      if (input.kind === 'heal_to_max') { next = effectiveMax; d = effectiveMax - current; }
      else { next = current + delta; if (next > effectiveMax) next = effectiveMax; if (next < 0) next = 0; d = delta; }
      return ok({ v_current: int(ec.v_current), encounter_creature_dead: dead }, { v_current: next }, input.kind === 'v' ? 'self_adjust_v' : 'heal_to_v_max', d);
    }
  }
  return { supported: false, refused: false, reason: 'unknown-kind' };
}

/** Lean write of an optional encounter_creatures scalar (build_encounter_creature_arr elides 0). */
function setLean(ec: Row, col: string, value: number, keepZero = false): void {
  if (value === 0 && !keepZero) delete ec[col]; else ec[col] = value;
}

const readVitality = createVitalityReader({ resolveInventoryInstance: () => null });
// The stack reader consumes the already-read family; it never walks a second holder.
const stacks = createStatusStackReader(contract.vitality.statusCatalog, {
  readStatuses: statuses => statuses, readItemField,
});
const blightedId = stacks.id('blighted')!;

/** Apply only saved/optimistic columns and their affected resource cluster in place.
 * Old relays may still supply a vitality block. Native held bases remain authoritative.
 */
export function applyResourceValues(row: Row, values: ResourceValues & { aps_total?: number | null }, _readers?: ResourceReaders, _serverVitality?: Row | null, catalog?: Row): void {
  const ec = asRow(row.encounter_creatures) ?? asRow(row.inventory) ?? (row.encounter_creatures = {}) as Row;
  const itemType = asRow(row.encounter_creatures) ? String(encounterCreatureField(ec, 'item_type') ?? readItemField(row, 'item_type') ?? '') : '';
  if ('aps_total' in values) ec.aps_total = compileCreatureApsTotal(itemType, values.aps_total);
  if ('aps_total' in values && !('aps' in values)) {
    const used = Math.max(0, int(ec.aps_total) - int(encounterCreatureField(ec, 'aps')));
    if (used) ec.aps_used = used; else delete ec.aps_used;
  }
  const vitalityChanges: ResourceValues = {};
  for (const [col, raw] of Object.entries(values) as [ResourceColumn, number][]) {
    const value = Math.trunc(Number(raw));
    if (!Number.isFinite(value)) continue;
    switch (col) {
      case 'aps': {
        const aps = Math.min(127, Math.max(0, value));
        ec.aps = aps;
        const total = compileCreatureApsTotal(itemType, encounterCreatureField(ec, 'aps_total'));
        ec.aps_total = total;
        const used = Math.max(0, total - aps);
        if (used > 0) ec.aps_used = used; else delete ec.aps_used;
        break;
      }
      case 'weapon_attack': {
        setLean(ec, col, value, true); // creature_build_rollups keeps weapon_attack = 0 on every encounter row
        const creature = asRow(asRow(row.computed)?.creature);
        if (creature) {
          const combat = (creature.combat ??= {}) as Row;
          const hindered = value - int(combat.extra_attack);
          if (hindered === 0) delete combat.hindered_attack_counter; else combat.hindered_attack_counter = hindered;
        }
        break;
      }
      case 'luck': setLean(ec, col, value, itemType !== 'player_character'); break; // finalize keeps a non-PC 0
      case 'mana': {
        // A non-PC 0 is kept: NULL is "never set" (starts at spirit), 0 is "spent" (owner 2026-09-27).
        setLean(ec, col, value, itemType !== 'player_character');
        const spirit = Number(scalar(ec.spirit) ?? 0);
        const pct = spirit !== 0 ? Math.floor((value / spirit) * 100) : 0;
        if (pct !== 0) ec.mana_percent = pct; else delete ec.mana_percent;
        break;
      }
      case 'reaction': case 'head_attack': case 'hunger_meter_current': case 'thirst_meter_current': case 'encounter_creature_dead':
        setLean(ec, col, value); break;
      case 'v_current': case 'v_temp': case 'v_max': case 'v_max_temp': {
        setLean(ec, col, value);
        vitalityChanges[col] = value;
        break;
      }
    }
  }
  if (!Object.keys(vitalityChanges).length) return;
  const vitalitySource = catalog ? { items: row.items, inventory: row.inventory, encounter_creatures: row.encounter_creatures,
    computed: row.computed, catalog } : row;
  patchVitality(row, vitalityChanges, {
    readVitality: () => readVitality(vitalitySource),
    readLayerValue: (value, key) => scalar((value as Row)[key]),
    readItemField: (_target, key) => readItemField(vitalitySource, key),
    readStatuses: target => storedStatusRows(target as Row, 'target'),
    readBlightedStack: (_target, statuses) => stacks.read(statuses, blightedId),
    buildStatusShell: () => null,
  });
}

const vitalityColumns = new Set(['v_current', 'v_max', 'v_temp', 'v_max_temp']);
const turnResourceColumns = new Set([...RESOURCE_KIND_COLUMNS.ooc_rest, 'aps_total']);
type VitalityTable = 'inventory' | 'encounter_creatures';
function vitalityField(row: Row, table: VitalityTable, key: string): unknown {
  return table === 'inventory' ? readInventoryField(row, key) : encounterCreatureField(row.encounter_creatures, key);
}
function vitalityNodes(value: unknown, table: VitalityTable): Row[] {
  if (Array.isArray(value)) return value.filter((row): row is Row => asRow(row) !== null);
  const host = asRow(value);
  if (!host) return [];
  if (table === 'encounter_creatures') return [host, ...normalizePhpListish(host.encounter_creatures_arr)]
    .filter((row): row is Row => asRow(row) !== null && asRow((row as Row).encounter_creatures) !== null);
  if (asRow(host.inventory)) return [host];
  const floor = readInventoryRows(host, 'encounter_inventories_arr');
  return floor.length ? floor : readInventoryRows(host, 'inventory');
}
/** Saved vitality echoes can repeat unchanged identity/cosmetic fields. Only
 * vitality and bookkeeping may differ; real equipment/shape/owner changes retain
 * their existing consumers. Compare against raw held inputs through their reader.
 */
export function savedVitalityRows(base: BaseEnvelope['base'], ops: readonly BasePatchOp[]): AppliedTouchedRow[] | null {
  return savedResourceRows(base, ops, vitalityColumns);
}
export function savedResourceRows(base: BaseEnvelope['base'], ops: readonly BasePatchOp[], columns: ReadonlySet<string>): AppliedTouchedRow[] | null {
  const table = ops[0]?.table;
  if (table !== 'inventory' && table !== 'encounter_creatures') return null;
  const identity = table === 'inventory' ? 'inventory_id' : 'encounter_creature_id';
  const nodes = vitalityNodes(base, table), rows: AppliedTouchedRow[] = [];
  for (const op of ops) {
    if (op.table !== table || op.op === 'delete' || op.inputs || op.catalog_inputs
      || !Object.keys(op.cols).some(key => columns.has(key))) return null;
    const node = nodes.find(row => String(vitalityField(row, table, identity) ?? '').toUpperCase() === op.row_id.toUpperCase());
    if (!node) return null;
    const posted = { [table]: op.cols };
    for (const key of Object.keys(op.cols)) {
      if (columns.has(key) || key === `${table === 'inventory' ? 'inventory' : 'encounter_creature'}_updated_by_id`
        || key === `${table === 'inventory' ? 'inventory' : 'encounter_creature'}_t_updated`
        || table === 'encounter_creatures' && ['movement_rev', 'movement_last_operation_id'].includes(key)) continue;
      const before = vitalityField(node, table, key), after = vitalityField(posted, table, key);
      const equal = ['x_pos', 'y_pos', 'z_pos'].includes(key) ? Number(before ?? 0) === Number(after ?? 0)
        : String(before ?? '') === String(after ?? '');
      if (!equal) return null;
    }
    rows.push({ table, row_id: op.row_id, partial: true, row: op.cols });
  }
  return rows.length ? rows : null;
}
/** Ordinary AP and out-of-combat rest saves relay raw columns after their resource
 * frames. Advance saved coverage and derived AP leaves without compiling the holder. */
export function savedTurnResourceFrame(envelope: BaseEnvelope, ops: readonly BasePatchOp[]): AppliedFrame | null {
  if (ops[0]?.table !== 'encounter_creatures') return null;
  const rows = savedResourceRows(envelope.base, ops, turnResourceColumns);
  return rows ? { kind: 'applied', operation_id: `turn-resource:${envelope.entity}:${envelope.entity_id}:${ops[0]!.base_rev}`,
    encounter_id: envelope.entity_id, actor_id: '', rev: 0, member_revs: {}, rows, computed: {}, new_entities: [], deletes: [] } : null;
}
/** Committed in-sequence resource frames patch raw inputs and display nodes in
 * place. This avoids the generic applied-tree walk and preserves resident rows.
 */
export function applyVitalityFrame(frame: AppliedFrame, source: unknown, raw: boolean): boolean {
  const turnResource = frame.operation_id.startsWith('turn-resource:');
  if (!(turnResource || frame.operation_id.startsWith('inventory-vitality:')) || frame.new_entities.length || frame.deletes.length
    || frame.new_inventory?.length || Object.keys(frame.inputs ?? {}).length
    || Object.keys(frame.member_revs).length || Object.keys(frame.computed).length) return false;
  const table = frame.rows[0]?.table;
  if (table !== 'inventory' && table !== 'encounter_creatures') return false;
  if (turnResource && table !== 'encounter_creatures') return false;
  const columnsToApply: ReadonlySet<string> = turnResource ? turnResourceColumns : vitalityColumns;
  const identity = table === 'inventory' ? 'inventory_id' : 'encounter_creature_id';
  const nodes = vitalityNodes(source, table), targets: [Row, AppliedTouchedRow][] = [];
  for (const incoming of frame.rows) {
    if (incoming.table !== table || !incoming.partial) return false;
    const node = nodes.find(row => String(vitalityField(row, table, identity) ?? '').toUpperCase() === incoming.row_id.toUpperCase());
    if (!node) return false;
    targets.push([node, incoming]);
  }
  const catalog = asRow(asRow(source)?.catalog) ?? undefined;
  for (const [node, incoming] of targets) {
    const columns = node[table] as Row;
    if (raw) { Object.assign(columns, incoming.row); continue; }
    const values: ResourceValues & { aps_total?: number | null } = {};
    for (const key of Object.keys(incoming.row)) {
      if (key === 'aps_total' && turnResource) values.aps_total = incoming.row[key] == null ? null : int(vitalityField({ [table]: incoming.row }, table, key));
      else if (columnsToApply.has(key)) values[key as ResourceColumn] = int(vitalityField({ [table]: incoming.row }, table, key));
      else columns[key] = incoming.row[key];
    }
    applyResourceValues(node, values, undefined, null, table === 'inventory' ? catalog : undefined);
  }
  return targets.length > 0;
}

// Retain the installed runtime's marker/import contract for inventory and creature echoes.
export { savedVitalityRows as savedInventoryVitalityRows, applyVitalityFrame as applyInventoryVitalityFrame };
